import { useCallback, useEffect, useState } from "react";

/**
 * Vista de plan simulada, EXCLUSIVA para administradores.
 *
 * Permite revisar la web como si la cuenta fuese Lite o Club Pro sin tocar el
 * plan real ni la facturación. Se guarda en el navegador y se sincroniza entre
 * todas las instancias montadas (barra superior, panel, muros) mediante un
 * evento propio, porque `storage` no se dispara en la misma pestaña.
 */
export type AdminPlanView = "lite" | "pro";

export const ADMIN_PLAN_VIEW_KEY = "rl-admin-plan-view";
const ADMIN_PLAN_VIEW_EVENT = "rl-admin-plan-view-change";

const readStored = (): AdminPlanView => {
  if (typeof window === "undefined") return "pro";
  return window.localStorage.getItem(ADMIN_PLAN_VIEW_KEY) === "lite" ? "lite" : "pro";
};

export function useAdminPlanView(enabled = true) {
  const [view, setViewState] = useState<AdminPlanView>("pro");

  useEffect(() => {
    if (!enabled) return;
    setViewState(readStored());
    const sync = () => setViewState(readStored());
    window.addEventListener(ADMIN_PLAN_VIEW_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(ADMIN_PLAN_VIEW_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [enabled]);

  const setView = useCallback((next: AdminPlanView) => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(ADMIN_PLAN_VIEW_KEY, next);
    setViewState(next);
    window.dispatchEvent(new Event(ADMIN_PLAN_VIEW_EVENT));
  }, []);

  return { view: enabled ? view : ("pro" as AdminPlanView), setView };
}
